import { useEffect, useLayoutEffect, useRef } from "react";
import type { MouseEvent, RefObject } from "react";

// The one focusable query every dialog shares. `:not([disabled])` sits on
// EVERY clause because a selector list is a union: without it a
// `<button disabled tabindex="0">` would still match via the tabindex clause
// and become an unfocusable `first`/`last` the trap can never intercept (#412).
const ENABLED_FOCUSABLES =
    'button:not([disabled]), select:not([disabled]), textarea:not([disabled]), input:not([disabled]), [href]:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';

interface UseDialogFocusTrapOptions {
    panelRef: RefObject<HTMLElement | null>;
    // Defaults to true: most dialogs using this mount only while open.
    isOpen?: boolean;
    // While true every dismissal (Escape, backdrop) is refused and focus is
    // parked on the panel, because the dialog's controls are disabled. A
    // dialog that stays dismissable mid-flight passes false: the trap below
    // still holds when one of its controls is disabled under focus.
    isProcessing: boolean;
    onRequestClose: () => void;
    // Where focus lands on open. Defaults to the control marked
    // `data-dialog-initial-focus`, else the first enabled focusable, or the
    // panel itself when there is none.
    initialFocusSelector?: string;
    // Focus the panel itself on open, ahead of any control in it.
    focusPanelOnOpen?: boolean;
    // Focused on close when the element that was focused before open has
    // left the document (e.g. the trigger was removed by the action) or is
    // disabled (e.g. while the page reloads behind the action).
    returnFocusRef?: RefObject<HTMLElement | null>;
    // Defaults to true. False: a backdrop press never dismisses; it keeps
    // focus inside the dialog instead (a money dialog must not be discarded
    // by a stray click).
    closeOnBackdropClick?: boolean;
}

interface DialogFocusTrap {
    backdropProps: {
        onMouseDown: (event: MouseEvent<HTMLElement>) => void;
        onClick: (event: MouseEvent<HTMLElement>) => void;
    };
    panelProps: {
        tabIndex: -1;
    };
}

// Rendered = has a box. Unlike `offsetParent` (null for every position:fixed
// element, which is what the drawers and sheets are), this is false only for
// a display:none subtree.
const isRendered = (element: Element): boolean =>
    element.getClientRects().length > 0;

// jsdom does no layout: there, nothing — not even <html> — has a client
// rect, so every visibility check below is skipped and all matched controls
// count. In a browser <html> always has a box.
const hasLayoutEngine = (): boolean => isRendered(document.documentElement);

const enabledFocusables = (panel: HTMLElement): HTMLElement[] => {
    const checkRendered = hasLayoutEngine();
    return Array.from(
        panel.querySelectorAll<HTMLElement>(ENABLED_FOCUSABLES),
    ).filter(
        (element) =>
            element.getAttribute("aria-disabled") !== "true" &&
            (!checkRendered || isRendered(element)),
    );
};

// Where focus goes by default: on open, when processing ends, and when a
// press on a non-dismissing backdrop pulls it back. A confirm dialog marks its
// safe back-out with `data-dialog-initial-focus` (on the control, or on a
// wrapper around it), so a stray Enter never confirms whatever the button
// order (#452). Unmarked, or with the marked control disabled, it is the first
// enabled control.
const defaultFocusTarget = (panel: HTMLElement): HTMLElement | undefined => {
    const focusables = enabledFocusables(panel);
    const marked = panel.querySelector("[data-dialog-initial-focus]");
    return (
        (marked && focusables.find((element) => marked.contains(element))) ||
        focusables[0]
    );
};

const isGroupedRadio = (element: HTMLElement): element is HTMLInputElement =>
    element instanceof HTMLInputElement &&
    element.type === "radio" &&
    element.name !== "";

// The browser's tab sequence over the panel's controls. A named radio group
// is ONE tab stop: Tab from any of its radios leaves the whole group, so a
// trailing group's last radio is not where Tab leaves the panel.
const tabStops = (focusables: HTMLElement[]): HTMLElement[][] => {
    const stops: HTMLElement[][] = [];
    for (const element of focusables) {
        const group = isGroupedRadio(element)
            ? stops.find(
                (stop) =>
                    isGroupedRadio(stop[0]) &&
                    stop[0].name === element.name &&
                    stop[0].form === element.form,
            )
            : undefined;
        if (group) {
            group.push(element);
        } else {
            stops.push([element]);
        }
    }
    return stops;
};

// Where focus lands entering a stop, as the browser does it (checked against
// Chromium): a group's checked radio either way; with none checked, its first
// radio going forward and its last going backward.
const entryOf = (stop: HTMLElement[], backward: boolean): HTMLElement =>
    stop.find((element) => isGroupedRadio(element) && element.checked) ??
    (backward ? stop[stop.length - 1] : stop[0]);

// Open traps in the order they opened. Only the last — the innermost of
// nested overlays, which open after their parent — handles keys.
const openTraps: object[] = [];

// Overlay chassis shared by every modal, drawer and sheet: scroll lock, focus
// into the panel on open and back out on close, a Tab/Shift+Tab trap over the
// ENABLED controls, Escape and backdrop dismissal, and a processing lockdown.
// Lifted from ClearChargeDialog.
//
// Keys are read on `window`, not on the backdrop: disabling or unmounting the
// focused control makes the browser drop focus to <body>, outside any element
// of the dialog, and from there a backdrop handler never sees Tab or Escape
// again. Tab with focus anywhere off the cycle re-enters it at the edge.
//
// The lockdown: while isProcessing focus is parked on the panel (tabIndex -1)
// and handed back to the first enabled control when processing ends with the
// dialog still open.
export const useDialogFocusTrap = ({
    panelRef,
    isOpen = true,
    isProcessing,
    onRequestClose,
    initialFocusSelector,
    focusPanelOnOpen = false,
    returnFocusRef,
    closeOnBackdropClick = true,
}: UseDialogFocusTrapOptions): DialogFocusTrap => {
    const pressedOnBackdrop = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const panel = panelRef.current;
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const initial =
            panel && !focusPanelOnOpen
                ? initialFocusSelector
                    ? panel.querySelector<HTMLElement>(initialFocusSelector)
                    : defaultFocusTarget(panel)
                : null;
        (initial ?? panel)?.focus();

        return () => {
            document.body.style.overflow = prevOverflow;
            // The action may have removed the trigger (a row's button that
            // only renders while pending) or disabled it (the page reloading
            // behind it); focusing a detached or disabled node is a silent
            // no-op that strands focus on <body>.
            if (
                previouslyFocused &&
                document.contains(previouslyFocused) &&
                !previouslyFocused.matches(":disabled")
            ) {
                previouslyFocused.focus();
            } else {
                // Resolved at close time by design: the return target may
                // mount after the dialog opened.
                // eslint-disable-next-line react-hooks/exhaustive-deps
                returnFocusRef?.current?.focus();
            }
        };
    }, [isOpen, initialFocusSelector, focusPanelOnOpen, panelRef, returnFocusRef]);

    const wasProcessing = useRef(false);
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const panel = panelRef.current;
        if (isProcessing) {
            panel?.focus();
        } else if (wasProcessing.current && panel) {
            defaultFocusTarget(panel)?.focus();
        }
        wasProcessing.current = isProcessing;
    }, [isOpen, isProcessing, panelRef]);

    // Read by the key listener at key time, so the listener is bound once per
    // open rather than re-bound for every new callback identity.
    const latest = useRef({ isProcessing, onRequestClose });
    useLayoutEffect(() => {
        latest.current = { isProcessing, onRequestClose };
    });

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const token = {};
        openTraps.push(token);
        const onKeyDown = (event: KeyboardEvent) => {
            const panel = panelRef.current;
            if (openTraps[openTraps.length - 1] !== token || !panel) {
                return;
            }
            // A mounted panel that is not rendered (an `md:hidden` sheet after
            // the viewport grew) is not a dialog the user can see: leave every
            // key to the page.
            if (hasLayoutEngine() && !isRendered(panel)) {
                return;
            }
            if (event.key === "Escape") {
                // A control that used Escape itself (an inline editor
                // cancelling) prevents it; an IME uses it to cancel a
                // composition.
                if (
                    event.defaultPrevented ||
                    event.isComposing ||
                    event.keyCode === 229
                ) {
                    return;
                }
                // Mid-flight the outcome must not be abandoned: a failure
                // needs an open dialog to land on. Cancel is disabled for the
                // same reason.
                if (!latest.current.isProcessing) {
                    latest.current.onRequestClose();
                }
                return;
            }
            if (event.key !== "Tab") {
                return;
            }
            const stops = tabStops(enabledFocusables(panel));
            if (stops.length === 0) {
                // Nothing is focusable (lockdown, or a spinner replaced the
                // control that held focus): park focus on the panel instead
                // of letting Tab leave the dialog.
                event.preventDefault();
                panel.focus();
                return;
            }
            const backward = event.shiftKey;
            const firstStop = stops[0];
            const lastStop = stops[stops.length - 1];
            const active = document.activeElement;
            const index =
                active instanceof HTMLElement
                    ? stops.findIndex((stop) => stop.includes(active))
                    : -1;
            if (index === -1) {
                // Focus is on the panel itself, on a control that was
                // disabled or removed under it, or lost to <body>: enter the
                // cycle at the edge instead of letting the browser walk out.
                event.preventDefault();
                entryOf(backward ? lastStop : firstStop, backward).focus();
                return;
            }
            if (backward && index === 0) {
                event.preventDefault();
                entryOf(lastStop, true).focus();
            } else if (!backward && index === stops.length - 1) {
                event.preventDefault();
                entryOf(firstStop, false).focus();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            openTraps.splice(openTraps.indexOf(token), 1);
        };
    }, [isOpen, panelRef]);

    // Only a press AND release both on the backdrop dismisses: a drag-select
    // that starts inside the panel and releases on the backdrop fires `click`
    // on the backdrop (the common ancestor) and must not discard the dialog.
    const onMouseDown = (event: MouseEvent<HTMLElement>) => {
        pressedOnBackdrop.current = event.target === event.currentTarget;
        const panel = panelRef.current;
        if (!closeOnBackdropClick && pressedOnBackdrop.current && panel) {
            // The press would drop focus to <body>; keep it where it is, and
            // bring it back inside if it had already left.
            event.preventDefault();
            if (!panel.contains(document.activeElement)) {
                (defaultFocusTarget(panel) ?? panel).focus();
            }
        }
    };

    const onClick = (event: MouseEvent<HTMLElement>) => {
        if (
            closeOnBackdropClick &&
            event.target === event.currentTarget &&
            pressedOnBackdrop.current &&
            !isProcessing
        ) {
            onRequestClose();
        }
    };

    return {
        backdropProps: { onMouseDown, onClick },
        panelProps: { tabIndex: -1 },
    };
};
